/* eslint-disable react/display-name */
import React, { useState, useMemo } from 'react';
import './index.scss';
import {
  AppBar,
  Toolbar,
  Container,
  Menu,
  MenuItem,
  Divider,
  LinearProgress,
  IconButton,
  Switch,
  FormControlLabel,
} from '@mui/material';
import CinchLogo from 'src/components/CinchLogo';
import { authconnect } from 'src/Auth';
import { connect } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { useIsAppComplete } from 'src/hooks';
import { useShowMobileBreadcrumbs } from 'src/hooks';
import { BrowserView, MobileView, isMobile } from 'react-device-detect';
import logo from 'src/assets/img/Cinch-Logos-03-noMargins-transparent.svg';
import CustomHeadroom from 'src/components/CustomHeadroom';
import clsx from 'clsx';
import mobileMenuImg from 'src/assets/img/Header/mobile-menu.svg';
import { LoanOfficerCard } from 'src/components/Header/LoanOfficerCard';
import Avatar from '@mui/material/Avatar';
import { useKiosk } from 'src/hooks';
import { useTranslation } from 'react-i18next';

const BorrowerApplicationHeader = React.lazy(
  () => import('src/components/Loan/Application/Header'),
);
const PostApplicationHeader = React.lazy(
  () => import('src/components/Loan/PostApplication/Header'),
);

const guidMatchStr =
  '[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}';

const LinkMenuItem = React.forwardRef((props, ref) => {
  const { to } = props;
  const renderLink = React.useMemo(
    () =>
      // eslint-disable-next-line react/display-name
      React.forwardRef((itemProps, ref) => (
        <Link
          to={to}
          {...itemProps}
          ref={ref}
          className={itemProps.className}
        />
      )),
    [to],
  );
  return <MenuItem component={renderLink} {...props} ref={ref} />;
});

const BorrowerHeader = ({ session, logout, working }) => {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [mobileNav, setMobileNav] = React.useState(false);
  const [languageAnchor, setLanguageAnchor] = useState(null);
  const { kioskMode, kioskView, toggleKioskView } = useKiosk();
  const { t } = useTranslation();
  const { i18n } = useTranslation();
  const appComplete = useIsAppComplete();
  const showMobileBreadcrumbs = useShowMobileBreadcrumbs();

  const location = useLocation();

  // Manual path matching like the old React Router v5 approach
  const pathMatch = location.pathname.match(
    new RegExp(`^/(apply|postapplication)/(${guidMatchStr})`),
  );

  let match = null;
  if (pathMatch) {
    match = {
      params: {
        section: pathMatch[1], // 'apply' or 'postapplication'
        guid: pathMatch[2], // the GUID
      },
    };
  }

  const closeMenu = () => {
    setMenuAnchor(null);
  };

  const stringAvatar = useMemo(() => {
    const name = session?.userFullName;
    if (!name?.trim()) return 'U';
    const parts = name.trim().split(' ').filter(Boolean);
    const first = parts[0] ? parts[0][0].toUpperCase() : '';
    const second = parts[1] ? parts[1][0].toUpperCase() : '';
    return `${first}${second}`;
  }, [session?.userFullName]);

  // Supported languages
  const languages = [
    { code: 'en', label: 'English' },
    { code: 'es', label: 'Español' },
  ];

  const handleLanguageMenu = (event) => {
    setLanguageAnchor(event.currentTarget);
  };

  const handleLanguageChange = (lng) => {
    i18n.changeLanguage(lng);
    setLanguageAnchor(null);
  };

  const closeLanguageMenu = () => {
    setLanguageAnchor(null);
  };
  return (
    <CustomHeadroom disable={kioskMode} className='borrower-header-wrapper'>
      <AppBar
        className={clsx('app-header', isMobile && 'mobile')}
        position='relative'
        elevation={0}
      >
        {working && <LinearProgress color='secondary' className='workstate' />}
        <Toolbar className='header-top'>
          <Container className='flex-container'>
            <BrowserView className='flex flex-container'>
              <Link to='/'>
                <img src={CinchLogo} alt='logo' className='logo' />
              </Link>
              {match &&
                (match.params.section === 'postapplication' ||
                  (match.params.section === 'apply' && appComplete)) && (
                  <React.Suspense fallback={<div>Loading...</div>}>
                    <PostApplicationHeader
                      isMobile={false}
                      mobileOpen={false}
                      onClose={() => {}}
                      guid={match.params.guid}
                    />
                  </React.Suspense>
                )}
              <span className='flex' />
              <IconButton
                color='inherit'
                onClick={handleLanguageMenu}
                aria-label='change language'
                sx={{ ml: 1 }}
              >
                <span role='img' aria-label='language'>
                  🌐
                </span>
              </IconButton>
              <Menu
                anchorEl={languageAnchor}
                open={Boolean(languageAnchor)}
                onClose={closeLanguageMenu}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                {languages.map((lang) => (
                  <MenuItem
                    key={lang.code}
                    selected={i18n.language.startsWith(lang.code)}
                    onClick={() => handleLanguageChange(lang.code)}
                  >
                    {lang.label}
                  </MenuItem>
                ))}
              </Menu>
              {kioskMode && (
                <FormControlLabel
                  control={(
                    <Switch
                      checked={kioskView}
                      onChange={(e) => toggleKioskView(e.target.checked)}
                    />
                  )}
                  label={'Quick Apply'}
                  title='Only include essential forms and questions'
                />
              )}
              <Avatar
                className='user-avatar'
                onClick={(e) => setMenuAnchor(e.currentTarget)}
              >
                {stringAvatar}
              </Avatar>
            </BrowserView>
            <MobileView className='flex flex-container'>
              {match &&
                (match.params.section === 'postapplication' ||
                  (match.params.section === 'apply' &&
                    !showMobileBreadcrumbs) ||
                  (match.params.section === 'apply' && appComplete)) && (
                  <IconButton
                    className='hamburger-menu'
                    onClick={() => setMobileNav(true)}
                    sx={{ p: 1 }}
                  >
                    <img src={mobileMenuImg} alt='mobile menu' />
                  </IconButton>
                )}
              <div className='flex flex-container flex-center'>
                <Link to='/' className='flex-container flex-center'>
                  <img className='logo' src={logo} alt='logo' />
                </Link>
              </div>
              <IconButton
                color='inherit'
                onClick={handleLanguageMenu}
                aria-label='change language'
                sx={{ ml: 1 }}
              >
                <span role='img' aria-label='language'>
                  🌐
                </span>
              </IconButton>
              <Menu
                anchorEl={languageAnchor}
                open={Boolean(languageAnchor)}
                onClose={closeLanguageMenu}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                {languages.map((lang) => (
                  <MenuItem
                    key={lang.code}
                    selected={i18n.language.startsWith(lang.code)}
                    onClick={() => handleLanguageChange(lang.code)}
                  >
                    {lang.label}
                  </MenuItem>
                ))}
              </Menu>
              {kioskMode && (
                <FormControlLabel
                  control={(
                    <Switch
                      checked={kioskView}
                      onChange={(e) => toggleKioskView(e.target.checked)}
                    />
                  )}
                  label={'Quick Apply'}
                  title='Only include essential forms and questions'
                />
              )}
              <Avatar
                className='user-avatar'
                onClick={(e) => setMenuAnchor(e.currentTarget)}
              >
                {stringAvatar}
              </Avatar>
            </MobileView>

            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={closeMenu}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              variant='menu'
            >
              <MenuItem disabled divider>
                {session?.userFullName}
              </MenuItem>
              {match && (
                <MenuItem divider>
                  <LoanOfficerCard loanGuid={match.params.guid} />
                </MenuItem>
              )}
              <LinkMenuItem to='/dashboard' onClick={closeMenu}>
                {t('postApplication.header.myLoans')}
              </LinkMenuItem>
              <LinkMenuItem to='/settings' onClick={closeMenu}>
                {t('postApplication.header.settings')}
              </LinkMenuItem>
              <Divider />
              <MenuItem onClick={() => logout(match?.params?.guid)}>
                {t('postApplication.header.logout')}
              </MenuItem>
            </Menu>
          </Container>
        </Toolbar>
      </AppBar>
      {match && match.params.section === 'apply' && (
        <React.Suspense fallback={<div>Loading...</div>}>
          <BorrowerApplicationHeader
            isMobile={isMobile}
            mobileOpen={mobileNav}
            onClose={() => setMobileNav(false)}
            guid={match.params.guid}
          />
        </React.Suspense>
      )}
      {match &&
        isMobile &&
        (match.params.section === 'postapplication' ||
          (match.params.section === 'apply' && appComplete)) && (
          <React.Suspense fallback={<div>Loading...</div>}>
            <PostApplicationHeader
              isMobile={isMobile}
              mobileOpen={mobileNav}
              onClose={() => setMobileNav(false)}
              guid={match.params.guid}
            />
          </React.Suspense>
        )}
    </CustomHeadroom>
  );
};

const mapStateToProps = ({ app: { working } }) => ({
  working: working.length > 0,
});

const mapAuthToProps = ({ session, logout, isAuthenticated }) => ({
  session,
  isAuthenticated:
    typeof isAuthenticated === 'function' ? isAuthenticated() : false,
  logout,
});

export default connect(mapStateToProps)(
  authconnect(mapAuthToProps)(BorrowerHeader),
);
